import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface Bootstrap {
  onboarding_completed: boolean;
  store_name: string;
  brand_description: string;
  product_type: string;
}

interface OnboardingState {
  isLoaded: boolean;
  isComplete: boolean;
}

/**
 * Checks whether the first-run Dippy welcome wizard has been completed
 * for this site. Used by AdminLayout to bounce new admins to
 * /admin/welcome before they see an empty revenue dashboard.
 *
 * Fire-and-forget semantics: on network error we assume "completed" and
 * let the admin through to the normal layout — the alternative (trapping
 * them in the welcome wizard on every request when the API is flaky) is
 * worse than a skipped onboarding.
 *
 * Dippy is a dropshipping-only concern. Non-dropship tenants (saas,
 * courses, services) short-circuit to complete so the redirect doesn't
 * trap them in a wizard they never opted into.
 */
export function useDippyOnboarding(): OnboardingState {
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const dropshipEnabled = isEnabled('dropshipping');
  const [state, setState] = useState<OnboardingState>({ isLoaded: false, isComplete: false });

  useEffect(() => {
    if (flagsLoading) return;
    if (!dropshipEnabled) {
      setState({ isLoaded: true, isComplete: true });
      return;
    }
    let cancelled = false;
    api
      .get<Bootstrap>('/admin/agent/welcome/bootstrap')
      .then((res) => {
        if (cancelled) return;
        setState({ isLoaded: true, isComplete: !!res.onboarding_completed });
      })
      .catch(() => {
        if (cancelled) return;
        // Assume completed on failure — don't trap users on a broken API.
        setState({ isLoaded: true, isComplete: true });
      });
    return () => { cancelled = true; };
  }, [flagsLoading, dropshipEnabled]);

  return state;
}
